import { makeAutoObservable } from 'mobx'
import { logger, prepareSearchPhoneNumber } from 'shared/lib'
import { type IDropdownItem, toastStore } from 'shared/ui'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { checkAccessPage, EnumAccessPageKeys } from 'entities/ExportRouters/lib/checkAccessPage'
import { InboxesApi } from 'entities/Inbox'
import { type IResponseInbox } from 'entities/Inbox/api/types'
import { Phone } from 'entities/Phone/model/Phone'
import {
  type INumbersGroupKey,
  type IMultiselectedItem,
} from 'pages/settings/pages/compliance/ui/MultiSelectNumbers/type'

export class SmartInboxStore {
  private _localmsg = false
  private _inbox: IResponseInbox | null = null
  private _deprioritizedNumberIds: number[] = []
  private _search = ''

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  init = (inbox: IResponseInbox) => {
    this._inbox = inbox
    this._localmsg = (inbox.numbers.length > 1 && inbox.is_geomatch) ?? false
    this._deprioritizedNumberIds = inbox.deprioritized_numbers?.map((id) => Number(id)) ?? []
  }

  get search() {
    return this._search
  }

  setSearch = (value: string) => {
    this._search = value
  }

  get localmsg() {
    return this._localmsg
  }

  get deprioritizedNumberIds() {
    return this._deprioritizedNumberIds
  }

  get inboxNumbers() {
    return this._inbox?.numbers ?? []
  }

  get multiSelectedValues() {
    return this.inboxNumbers
      .filter((number) => this._deprioritizedNumberIds.includes(number.id))
      .map((number) => ({
        id: number.id,
        label: number.formatted_number,
        name: '',
        data: new Phone(number, this._entities),
      }))
  }

  get isTheSameDeprioritizedNumberIds() {
    const previousIds = this._inbox?.deprioritized_numbers?.map((id) => Number(id)) ?? []
    if (previousIds.length !== this._deprioritizedNumberIds.length) {
      return false
    }
    return this._deprioritizedNumberIds.every((id) => previousIds.includes(id))
  }

  get numbersGroups() {
    const unselectedCount = this.inboxNumbers.length - this._deprioritizedNumberIds.length

    const filteredNumbers = this.inboxNumbers.filter((number) => {
      const isSelected = this._deprioritizedNumberIds.includes(number.id)
      const cleanNumber = prepareSearchPhoneNumber(number.number)
      const searchTerm = prepareSearchPhoneNumber(this._search)
      const matchesSearch = cleanNumber.includes(searchTerm)

      return !isSelected && matchesSearch
    })

    return [
      {
        key: 'assigned' as INumbersGroupKey,
        title: '',
        items: filteredNumbers.map((number) => {
          const isDisabled = unselectedCount <= 1
          let labelRight = ''
          if (number.is_local) {
            let address = [number.city, number.state].filter(Boolean).join(', ')
            const area_code = number.area_code

            if (!address && area_code?.state_name && area_code?.state_code) {
              address = `${area_code.state_name}, ${area_code.state_code}`
            }
            labelRight = address
          }

          return {
            id: number.id,
            label: number.formatted_number,
            labelRight,
            disabled: isDisabled,
            data: number,
          }
        }),
      },
    ]
  }

  get shouldShowAddNumberFlow() {
    return this._inbox?.numbers.length === 1
  }

  get isInboxOwner() {
    return this._inbox?.owner_id === this._entities.usersStore.user?.id
  }

  get isManageOrganizationInboxes() {
    return checkAccessPage(EnumAccessPageKeys.inbox_page, this._entities)
  }

  get isBillingSettingsAccess() {
    return this._entities.userPermissionsStore.canManageBilling
  }

  get isUserCanManageOrganizationInboxes() {
    if (this.shouldShowAddNumberFlow && !this.isBillingSettingsAccess) {
      return false
    }
    return this.isInboxOwner || this.isManageOrganizationInboxes
  }

  toggleLocalmsg = () => {
    this._localmsg = !this._localmsg
    this.updateLocalmsg()
  }

  setDeprioritizedNumberIds = (ids: number[]) => {
    this._deprioritizedNumberIds = ids
  }

  handleMultiSelect = (item: IDropdownItem) => {
    this._deprioritizedNumberIds.push(Number(item.id))
  }

  removeMultiSelectedItem = (item: IMultiselectedItem) => {
    this._deprioritizedNumberIds = this._deprioritizedNumberIds.filter((id) => id !== item.id)
  }

  updateDeprioritizeNumbers = async () => {
    if (!this._inbox?.id || this.isTheSameDeprioritizedNumberIds) {
      return
    }
    try {
      const { data } = await InboxesApi.updateInboxDeprioritizedNumbers(this._inbox.id, {
        deprioritized_numbers: this._deprioritizedNumberIds,
      })
      this._inbox = data
    } catch (error) {
      logger.error(error)
      toastStore.add({
        type: 'error',
        title: 'Failed to update deprioritized numbers',
      })
    }
  }

  updateLocalmsg = async () => {
    if (!this._inbox?.id) {
      return
    }

    try {
      await InboxesApi.updateInboxGeomatch(this._inbox.id, {
        is_geomatch: this._localmsg,
      })
      toastStore.add({
        type: 'success',
        title: this._localmsg ? 'Localmsg activated' : 'Localmsg deactivated',
      })
    } catch (error) {
      this._localmsg = !this._localmsg
      logger.error(error)
      toastStore.add({
        type: 'error',
        title: 'Failed to update localmsg',
      })
    }
  }
}
